import {
  Building2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import {
  ContactInfo,
  ContactUsService,
} from "@/services/landing/contactUsService";

export default function ContactUs() {
  const [form, setForm] = useState({
    email: "",
    honeypot: "",
    message: "",
    name: "",
    subject: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<null | string>(null);
  const [submitSuccess, setSubmitSuccess] = useState<null | string>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  // Theme adaptive state
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        setLoading(true);
        const data = await ContactUsService.get();
        setContactInfo(data);
      } catch (err) {
        console.error("Failed to fetch contact info:", err);
        setError("Failed to load contact information. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    loadContactInfo();
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributeFilter: ["class"],
      attributes: true,
    });
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check - if filled, it's likely a bot
    if (form.honeypot) {
      console.warn("Spam detected via honeypot");
      setSubmitted(true);
      setSubmitSuccess("Your message has been sent successfully!");
      setTimeout(() => {
        setSubmitted(false);
        setSubmitSuccess(null);
      }, 3000);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      // Get reCAPTCHA token if configured
      let recaptchaToken = "";
      if (window.grecaptcha && import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
        try {
          recaptchaToken = await window.grecaptcha.execute(
            import.meta.env.VITE_RECAPTCHA_SITE_KEY,
            { action: "contact_form" },
          );
        } catch (err) {
          console.warn("reCAPTCHA not available:", err);
        }
      }

      const { honeypot, ...formData } = form;
      const response = await ContactUsService.submitForm({
        ...formData,
        recaptchaToken,
      });

      setSubmitted(true);
      setSubmitSuccess(
        response.message || "Your message has been sent successfully!",
      );
      setForm({ email: "", honeypot: "", message: "", name: "", subject: "" });
      setTimeout(() => {
        setSubmitted(false);
        setSubmitSuccess(null);
      }, 5000);
    } catch (err: any) {
      console.error("Failed to submit form:", err);
      setSubmitError(
        err.response?.data?.message ||
          "Failed to send your message. Please try again later.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Parse hours JSON string and format for display
  const getFormattedHours = () => {
    if (!contactInfo?.hours) return "Not available";

    try {
      const hours =
        typeof contactInfo.hours === "string"
          ? JSON.parse(contactInfo.hours)
          : contactInfo.hours;

      const daysWithHours = Object.entries(hours)
        .filter(([_, times]: [string, any]) => times.open && times.close)
        .map(([day, times]: [string, any]) => ({
          close: times.close,
          day,
          open: times.open,
        }));

      if (daysWithHours.length === 0) return "Closed";

      // Group consecutive days with same hours
      const groupedHours: { [key: string]: string[] } = {};
      daysWithHours.forEach(({ close, day, open }) => {
        const timeKey = `${open}-${close}`;
        if (!groupedHours[timeKey]) groupedHours[timeKey] = [];
        groupedHours[timeKey].push(day);
      });

      return Object.entries(groupedHours)
        .map(([time, days]) => {
          const [open, close] = time.split("-");
          const dayRange =
            days.length > 1 && days[0] !== days[days.length - 1]
              ? `${days[0]}-${days[days.length - 1]}`
              : days.join(", ");
          return `${dayRange}: ${open} – ${close}`;
        })
        .join("\n");
    } catch (e) {
      console.error("Failed to parse hours:", e);
      return "Not available";
    }
  };

  return (
    <>
      <div className="relative z-50">
        <AppNavbar />
      </div>
      <div
        className={`min-h-screen pt-16 ${isDark ? "bg-base-300" : "bg-base-200"}`}
      >
        <Breadcrumbs />

        <section
          className={`relative overflow-hidden ${
            isDark
              ? "bg-base-300 text-base-content"
              : "bg-base-100 text-base-content"
          }`}
        >
          <div className="absolute inset-0 z-10 opacity-10">
            <img
              alt="Contact background"
              className="h-full w-full object-cover"
              src="https://images.unsplash.com/photo-1465101046530-73398c7f28ca?w=1200&h=600&fit=crop&crop=center"
              style={{
                filter: isDark ? "brightness(0.7)" : "none",
              }}
            />
          </div>
          <div className="relative z-20 mx-auto max-w-7xl px-4 py-20">
            <div className="mb-12 text-center">
              <h1 className="mb-4 text-5xl font-bold">Contact Us</h1>
              <p className="text-xl opacity-90">
                We’d love to hear from you. Reach out for support, partnerships,
                or feedback.
              </p>
            </div>
            <div className="grid gap-12 md:grid-cols-2">
              {/* Contact Info */}
              {loading ? (
                <div className="flex items-center justify-center">
                  <span className="loading loading-spinner loading-lg"></span>
                </div>
              ) : error ? (
                <div className="alert alert-error">
                  <span>{error}</span>
                </div>
              ) : contactInfo ? (
                <div className="flex flex-col justify-center gap-8">
                  <div className="flex items-center gap-4">
                    <Building2 className="text-accent" size={32} />
                    <div>
                      <div className="font-bold">Hareeng HQ</div>
                      <div
                        className={`${
                          isDark
                            ? "text-base-content/60"
                            : "text-base-content/70"
                        }`}
                      >
                        {contactInfo.address}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Mail className="text-primary" size={32} />
                    <div>
                      <div className="font-bold">Email</div>
                      <a
                        className={`${
                          isDark
                            ? "text-base-content/60"
                            : "text-base-content/70"
                        } hover:underline`}
                        href={`mailto:${contactInfo.email}`}
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Phone className="text-success" size={32} />
                    <div>
                      <div className="font-bold">Phone</div>
                      <a
                        className={`${
                          isDark
                            ? "text-base-content/60"
                            : "text-base-content/70"
                        } hover:underline`}
                        href={`tel:${contactInfo.phone.replace(/\s/g, "")}`}
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <MapPin className="text-warning" size={32} />
                    <div>
                      <div className="font-bold">Support Hours</div>
                      <div
                        className={`${
                          isDark
                            ? "text-base-content/60"
                            : "text-base-content/70"
                        }`}
                        style={{ whiteSpace: "pre-line" }}
                      >
                        {getFormattedHours()}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              {/* Contact Form */}
              <form
                className={`flex flex-col gap-6 rounded-2xl p-8 shadow-2xl ${isDark ? "bg-base-200" : "bg-base-100"}`}
                onSubmit={handleSubmit}
              >
                {/* Honeypot field - hidden from users, bots will fill it */}
                <div
                  aria-hidden="true"
                  style={{
                    left: "-9999px",
                    opacity: 0,
                    pointerEvents: "none",
                    position: "absolute",
                  }}
                >
                  <label htmlFor="url">Website URL</label>
                  <input
                    autoComplete="url"
                    id="url"
                    name="honeypot"
                    onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                    tabIndex={-1}
                    type="url"
                    value={form.honeypot}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    className={`flex items-center gap-2 font-semibold ${
                      isDark ? "text-base-content" : "text-base-content"
                    }`}
                    htmlFor="name"
                  >
                    <User size={18} /> Name
                  </label>
                  <input
                    className={`input input-bordered ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                    id="name"
                    name="name"
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    type="text"
                    value={form.name}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    className={`flex items-center gap-2 font-semibold ${
                      isDark ? "text-base-content" : "text-base-content"
                    }`}
                    htmlFor="email"
                  >
                    <Mail size={18} /> Email
                  </label>
                  <input
                    className={`input input-bordered ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                    id="email"
                    name="email"
                    onChange={handleChange}
                    placeholder="you@email.com"
                    required
                    type="email"
                    value={form.email}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    className={`flex items-center gap-2 font-semibold ${
                      isDark ? "text-base-content" : "text-base-content"
                    }`}
                    htmlFor="subject"
                  >
                    <MessageCircle size={18} /> Subject
                  </label>
                  <input
                    className={`input input-bordered ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                    id="subject"
                    name="subject"
                    onChange={handleChange}
                    placeholder="Subject"
                    required
                    type="text"
                    value={form.subject}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    className={`flex items-center gap-2 font-semibold ${
                      isDark ? "text-base-content" : "text-base-content"
                    }`}
                    htmlFor="message"
                  >
                    <Send size={18} /> Message
                  </label>
                  <textarea
                    className={`textarea textarea-bordered min-h-[120px] ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                    id="message"
                    name="message"
                    onChange={handleChange}
                    placeholder="Type your message here..."
                    required
                    value={form.message}
                  />
                </div>
                <button
                  className="btn btn-primary btn-lg flex items-center gap-2"
                  disabled={submitting}
                  type="submit"
                >
                  <Send size={20} />
                  {submitting ? "Sending..." : "Send Message"}
                </button>
                {submitError && (
                  <div className="alert alert-error">
                    <span>{submitError}</span>
                  </div>
                )}
                {submitSuccess && (
                  <div className="alert alert-success">
                    <span>{submitSuccess}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
